import React, { FC, FormEvent } from 'react';
import { Box, Button, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Stack } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@hooks/redux-custom';
import { DeliveryTimeType, FutureOrderDateTimes } from '@models';
import { setDeliveryTime } from '@store/slices/order';
import { useGetSuburbHoursQuery } from '@services';
import { EcomTradingDateTimePicker } from '@components';

type Props = {
  handleBack: () => void;
  handleNext: () => void;
};

export const DeliveryTimeSelection: FC<Props> = ({ handleBack, handleNext }) => {
  const [type, setType] = React.useState<DeliveryTimeType>(DeliveryTimeType.ASAP);
  const [deliveryDateTime, setDeliveryDateTime] = React.useState<FutureOrderDateTimes>({});
  const dispatch = useAppDispatch();
  const { shippingAddress } = useAppSelector((state) => state.order.value);

  const { data: deliverySuburb } = useGetSuburbHoursQuery({
    suburb: `${shippingAddress?.postal_code}_${shippingAddress?.city.toLowerCase().replace(/\s/g, '')}`,
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (type === DeliveryTimeType.ASAP) {
      dispatch(
        setDeliveryTime({
          type: DeliveryTimeType.ASAP,
          requested_delivery_time: new Date().toISOString(),
          requested_delivery_end_time: undefined,
        }),
      );
    }

    if (type === DeliveryTimeType.Future) {
      deliveryDateTime &&
        dispatch(
          setDeliveryTime({
            type: DeliveryTimeType.Future,
            requested_delivery_time: deliveryDateTime.requested_delivery_time,
            requested_delivery_end_time: deliveryDateTime.requested_delivery_end_time,
          }),
        );
    }

    if (type === DeliveryTimeType.ASAP || deliveryDateTime.requested_delivery_end_time) {
      handleNext();
    }
  };

  return (
    <form onSubmit={(e) => onSubmit(e)}>
      <Stack spacing={2} marginLeft={1} marginTop={2}>
        <FormControl component="fieldset">
          <FormLabel component="legend">When would you like this order delivered?</FormLabel>
          <RadioGroup row onChange={(e) => setType(e.target.value as DeliveryTimeType)} defaultValue={DeliveryTimeType.ASAP}>
            <FormControlLabel control={<Radio />} label="ASAP" value={DeliveryTimeType.ASAP} data-testid="asap-radio" />
            <FormControlLabel control={<Radio />} label="Future Order" value={DeliveryTimeType.Future} data-testid="future-order-radio" />
          </RadioGroup>
        </FormControl>
        {type === DeliveryTimeType.Future && deliverySuburb && (
          <EcomTradingDateTimePicker deliverySuburb={deliverySuburb} onSelect={(dateTime) => setDeliveryDateTime(dateTime)} />
        )}
      </Stack>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2 }}>
        <Button onClick={handleBack} sx={{ mr: 1 }} data-testid="delivery-time-back-btn">
          Back
        </Button>
        <Button type="submit" data-testid="delivery-time-next-btn">
          Next
        </Button>
      </Box>
    </form>
  );
};
